import { tr } from '@/lib/i18n-core';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, dateTime } from '@/lib/utils';
import { DataTable, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Input, Select, TableSkeleton } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { CancelSheetDialog, CloseSheetDialog, SettleLineDialog } from './StockTakeDialogs';
import { StockTakeVariancePanel } from './StockTakeVariancePanel';
import { Pager, StockStatusBadge, formatQty, invalidateStockControl, parseQty } from './stock-control-common';
import {
  canResolveLine,
  disputedOf,
  disputesSavedBy,
  disputesVisible,
  isStockApprover,
  shownLineStatus,
} from './stock-take-access';
import {
  COUNT_SAVE_CHUNK,
  LINES_PAGE_SIZE,
  type CountSaveResult,
  type StockTakeDetail,
  type StockTakeLine,
  type StockTakeLines,
} from './stock-control-types';

type SheetView = 'count' | 'variance';

interface CountPayloadEntry {
  lineId: string;
  qty: number;
  countedAt?: string;
}

function savedCountText(line: StockTakeLine): string {
  return line.countedQty === null ? '' : String(line.countedQty);
}

function focusNextCountInput(event: KeyboardEvent<HTMLInputElement>): void {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  const nextRow = event.currentTarget.closest('tr')?.nextElementSibling;
  nextRow?.querySelector<HTMLInputElement>('input[data-count-input]')?.focus();
}

export function StockTakeSheet({
  takeId,
  onBack,
  onDirtyChange,
}: {
  takeId: string;
  onBack: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = can('stock.take.manage');
  const approver = isStockApprover(can);

  const [view, setView] = useState<SheetView>('count');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [entries, setEntries] = useState<Record<string, string>>({});
  const [countedAt, setCountedAt] = useState<Record<string, string>>({});
  const [settling, setSettling] = useState<StockTakeLine | null>(null);
  const [closing, setClosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const header = useQuery({
    queryKey: ['stock-control-take', takeId],
    queryFn: () => get<StockTakeDetail>(`/pharmacy/stock-takes/${takeId}`),
  });

  const lines = useQuery({
    queryKey: ['stock-control-take-lines', takeId, statusFilter, search, offset],
    queryFn: () =>
      get<StockTakeLines>(
        `/pharmacy/stock-takes/${takeId}/lines${qs({
          status: statusFilter || undefined,
          q: search || undefined,
          limit: LINES_PAGE_SIZE,
          offset,
        })}`,
      ),
    enabled: view === 'count',
    placeholderData: keepPreviousData,
  });

  const detail = header.data;
  const isOpen = detail?.status === 'open';
  const canCount = canManage && isOpen;
  const showDisputes = detail ? disputesVisible(detail, approver) : false;

  const typedEntries = Object.entries(entries).filter(([, text]) => text.trim() !== '');
  const unsavedEntries: CountPayloadEntry[] = typedEntries.flatMap(([lineId, text]) => {
    const qty = parseQty(text);
    return qty !== null && qty >= 0 ? [{ lineId, qty, ...(countedAt[lineId] ? { countedAt: countedAt[lineId] } : {}) }] : [];
  });
  const invalidCount = typedEntries.length - unsavedEntries.length;
  const hasUnsaved = typedEntries.length > 0;

  useEffect(() => {
    onDirtyChange(hasUnsaved);
  }, [hasUnsaved, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  const forgetEntries = (lineIds: string[]) => {
    setEntries((current) => {
      const next = { ...current };
      for (const lineId of lineIds) delete next[lineId];
      return next;
    });
    setCountedAt((current) => {
      const next = { ...current };
      for (const lineId of lineIds) delete next[lineId];
      return next;
    });
  };

  const setCount = (line: StockTakeLine, text: string) => {
    const reverted = text === savedCountText(line);
    setEntries((current) => {
      const next = { ...current };
      if (reverted) delete next[line.id];
      else next[line.id] = text;
      return next;
    });
    setCountedAt((current) => {
      const next = { ...current };
      if (reverted) delete next[line.id];
      else next[line.id] = new Date().toISOString();
      return next;
    });
  };

  const saveCounts = useMutation({
    mutationFn: async (payload: CountPayloadEntry[]) => {
      const savedIds: string[] = [];
      let saved = 0;
      let disputed = 0;
      try {
        for (let start = 0; start < payload.length; start += COUNT_SAVE_CHUNK) {
          const chunk = payload.slice(start, start + COUNT_SAVE_CHUNK);
          const result = await post<CountSaveResult>(`/pharmacy/stock-takes/${takeId}/counts`, { entries: chunk });
          savedIds.push(...chunk.map((entry) => entry.lineId));
          saved += result.saved;
          disputed += disputesSavedBy(result);
        }
      } finally {
        if (savedIds.length > 0) {
          await invalidateStockControl(queryClient);
          forgetEntries(savedIds);
        }
      }
      return { saved, disputed };
    },
    onSuccess: (result) => {
      const detailText =
        result.disputed > 0
          ? `${result.disputed} line${result.disputed === 1 ? ' is' : 's are'} now disputed because a second counter found a different figure.`
          : undefined;
      toast.success(`${result.saved} count${result.saved === 1 ? '' : 's'} saved`, detailText);
    },
    onError: (caught) => toast.error(tr('pharmacy', 'could_not_save_the_counts'), describeError(caught)),
  });

  const runSearch = () => {
    setSearch(searchText.trim());
    setOffset(0);
  };

  const goBack = () => {
    if (hasUnsaved && !window.confirm(tr('pharmacy', 'you_have_counts_that_are_not_saved_leave_this_2'))) return;
    onBack();
  };

  if (header.isLoading) {
    return (
      <TableSkeleton />
    );
  }

  if (header.error || !detail) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> {tr('pharmacy', 'all_count_sheets')}
        </Button>
        <Alert variant="destructive">{describeError(header.error)}</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" className="-ms-2 mb-1" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" /> {tr('pharmacy', 'all_count_sheets')}
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">{detail.number}</h2>
            <StockStatusBadge status={detail.status} />
            {detail.blind && <Badge variant="secondary">{tr('pharmacy', 'blind_count_2')}</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {detail.store.name} {tr('pharmacy', 'snapshot_taken')} {dateTime(detail.snapshotAt)} · {detail.lineCount} {tr('pharmacy', 'line')}
            {detail.lineCount === 1 ? '' : 's'}
            {detail.scopeNote ? ` · ${detail.scopeNote}` : ''}
          </p>
          {detail.notes && <p className="mt-0.5 text-sm">{detail.notes}</p>}
          {detail.adjustmentRequests.length > 0 && (
            <p className="mt-1 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              {tr('pharmacy', 'adjustment')}
              {detail.adjustmentRequests.map((request) => (
                <span key={request.id} className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs">{request.number}</span>
                  <StockStatusBadge status={request.status} />
                </span>
              ))}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canCount && view === 'count' && (
            <Button
              onClick={() => saveCounts.mutate(unsavedEntries)}
              loading={saveCounts.isPending}
              disabled={unsavedEntries.length === 0 || invalidCount > 0}
            >
              {tr('pharmacy', 'save_counts')}{unsavedEntries.length > 0 ? ` (${unsavedEntries.length})` : ''}
            </Button>
          )}
          {canManage && isOpen && (
            <>
              <Button variant="outline" onClick={() => setClosing(true)} disabled={saveCounts.isPending}>
                {tr('pharmacy', 'close_sheet')}
              </Button>
              <Button variant="ghost" onClick={() => setCancelling(true)} disabled={saveCounts.isPending}>
                {tr('pharmacy', 'cancel_sheet')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={cn('grid gap-3', showDisputes ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        <StatTile
          label={tr('pharmacy', 'not_counted_2')}
          value={detail.progress.pending}
          tone={detail.progress.pending > 0 && isOpen ? 'warning' : 'default'}
        />
        <StatTile label={tr('pharmacy', 'counted_2')} value={detail.progress.counted} tone="success" />
        {showDisputes && (
          <StatTile
            label={tr('pharmacy', 'disputed')}
            value={disputedOf(detail.progress)}
            sub={disputedOf(detail.progress) > 0 ? tr('pharmacy', 'two_counters_disagree_settle_each_one_before') : undefined}
            tone={disputedOf(detail.progress) > 0 ? 'destructive' : 'default'}
          />
        )}
      </div>

      <Tabs
        active={view}
        onChange={(id) => setView(id === 'variance' ? 'variance' : 'count')}
        tabs={[
          { id: 'count', label: tr('pharmacy', 'count') },
          { id: 'variance', label: tr('pharmacy', 'variance') },
        ]}
      />

      {view === 'variance' && <StockTakeVariancePanel takeId={takeId} />}

      {view === 'count' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-2">
              <Input
                className="w-72"
                placeholder={tr('pharmacy', 'search_name_code_barcode_or_batch_then_press')}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  runSearch();
                }}
              />
              <Button variant="secondary" onClick={runSearch} loading={lines.isFetching && !lines.isPlaceholderData}>
                {tr('pharmacy', 'search')}
              </Button>
            </div>
            <Select
              className="w-44"
              aria-label={tr('pharmacy', 'filter_lines_by_status')}
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">{tr('pharmacy', 'all_lines')}</option>
              <option value="pending">{tr('pharmacy', 'not_counted_2')}</option>
              <option value="counted">{tr('pharmacy', 'counted_2')}</option>
              {showDisputes && <option value="disputed">{tr('pharmacy', 'disputed')}</option>}
            </Select>
          </div>

          {canCount && (
            <Alert variant={hasUnsaved ? 'warning' : 'default'}>
              {hasUnsaved
                ? `${typedEntries.length} count${typedEntries.length === 1 ? '' : 's'} typed but not saved${
                    invalidCount > 0 ? `, ${invalidCount} not a valid quantity` : ''
                  }. They stay here while you search, filter or change pages. Nothing is sent until you press Save counts.`
                : tr('pharmacy', 'type_what_you_count_and_press_enter_to_move')}
            </Alert>
          )}

          <DataTable
            rows={lines.data?.lines}
            loading={lines.isLoading} refreshing={lines.isFetching}
            error={lines.error}
            rowKey={(line) => line.id}
            empty={
              search || statusFilter
                ? tr('pharmacy', 'no_lines_match_that_search_or_filter')
                : tr('pharmacy', 'this_sheet_has_no_lines_the_store_had_no_stock')
            }
            columns={[
              {
                header: tr('pharmacy', 'product'),
                cell: (line) => (
                  <div>
                    <p className="font-medium">{`${line.product.genericName} ${line.product.strength ?? ''}`.trim()}</p>
                    <p className="text-xs text-muted-foreground">{line.product.code}</p>
                  </div>
                ),
              },
              { header: tr('pharmacy', 'batch'), cell: (line) => <span className="font-mono text-xs">{line.batchNo}</span> },
              { header: tr('pharmacy', 'expires'), cell: (line) => dateOnly(line.expiryDate) },
              {
                header: tr('pharmacy', 'system'),
                align: 'right',
                cell: (line) =>
                  line.systemQty === null ? (
                    <span className="text-xs text-muted-foreground">{tr('pharmacy', 'blind_2')}</span>
                  ) : (
                    `${formatQty(line.systemQty)} ${line.product.unit}`
                  ),
              },
              {
                header: tr('pharmacy', 'counted_2'),
                align: 'right',
                cell: (line) => {
                  if (!canCount) return line.countedQty === null ? '—' : formatQty(line.countedQty);
                  const typed = entries[line.id];
                  const hasEntry = typed !== undefined;
                  const invalid = hasEntry && typed.trim() !== '' && (parseQty(typed) === null || (parseQty(typed) ?? 0) < 0);
                  return (
                    <Input
                      data-count-input
                      className={cn(
                        'ms-auto w-28 text-end tabular',
                        hasEntry && 'border-warning',
                        invalid && 'border-destructive',
                      )}
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      aria-label={tr('pharmacy', 'counted_quantity_for_x_batch_x', { genericName: line.product.genericName, batchNo: line.batchNo })}
                      aria-invalid={invalid}
                      disabled={saveCounts.isPending}
                      value={typed ?? savedCountText(line)}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setCount(line, event.target.value)}
                      onKeyDown={focusNextCountInput}
                    />
                  );
                },
              },
              {
                header: tr('pharmacy', 'status'),
                cell: (line) => (
                  <div>
                    <StockStatusBadge status={shownLineStatus(line.status, showDisputes)} />
                    {line.resolutionNote && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{tr('pharmacy', 'settled')} {line.resolutionNote}</p>
                    )}
                  </div>
                ),
              },
              {
                header: tr('pharmacy', 'counted_by'),
                cell: (line) =>
                  line.counts.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <ul className="text-xs">
                      {line.counts.map((entry) => (
                        <li key={entry.userId}>
                          {entry.userName}: <strong>{formatQty(entry.qty)}</strong>
                        </li>
                      ))}
                    </ul>
                  ),
              },
              {
                header: '',
                align: 'right',
                cell: (line) =>
                  canResolveLine(approver, isOpen, line.status) ? (
                    <Button size="sm" variant="outline" onClick={() => setSettling(line)}>
                      {tr('pharmacy', 'settle')}
                    </Button>
                  ) : null,
              },
            ]}
          />
          <Pager
            offset={offset}
            pageSize={LINES_PAGE_SIZE}
            rowCount={lines.data?.lines.length ?? 0}
            disabled={lines.isFetching || saveCounts.isPending}
            onOffsetChange={setOffset}
          />
        </>
      )}

      {settling && <SettleLineDialog takeId={takeId} line={settling} onClose={() => setSettling(null)} />}
      {closing && (
        <CloseSheetDialog sheet={detail} unsavedCount={typedEntries.length} onClose={() => setClosing(false)} />
      )}
      {cancelling && <CancelSheetDialog sheet={detail} onClose={() => setCancelling(false)} />}
    </div>
  );
}
